import { useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91吃瓜网',
    url: 'https://91chigua.com',
    description: '最新热点吃瓜爆料聚合平台，专注娱乐圈、社会热点、明星八卦等吃瓜资讯',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@91chigua.com',
      availableLanguage: 'Chinese',
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9pijnmb4qjhggaepv3g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || '';
      const isSpam = serverMsg.includes('spam') || responseText.includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>

      <div className="min-h-screen pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Hero Banner */}
          <section className="mt-4 relative w-full h-[280px] md:h-[340px] rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20workspace%20with%20warm%20coral%20and%20amber%20accent%20colors%2C%20collaborative%20team%20environment%2C%20digital%20media%20company%20headquarters%2C%20soft%20natural%20lighting%2C%20elegant%20interior%20design%2C%20happy%20team%20atmosphere&width=1400&height=400&seq=about-hero&orientation=landscape"
              alt="关于91吃瓜网"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-black/30"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-background-50">关于91吃瓜网</h1>
              <p className="mt-3 text-sm md:text-base text-background-50/80 max-w-lg">
                最新热点吃瓜爆料聚合平台
              </p>
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="mt-12">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 text-center mb-8">品牌介绍</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-foreground-900 mb-3">我们是谁</h3>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  91吃瓜网成立于2024年，是国内领先的吃瓜爆料聚合平台。我们致力于为吃瓜群众提供最新、最全、最劲爆的瓜料资讯，覆盖娱乐圈八卦、社会热点事件、明星动态、网红爆料等多个领域。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed mt-3">
                  我们的编辑团队由资深娱乐记者、社会观察员和数据分析师组成，通过多渠道信息采集和交叉验证，确保每一条瓜料资讯的真实性和时效性。
                </p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-foreground-900 mb-3">我们的使命</h3>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  在这个信息爆炸的时代，吃瓜已成为一种生活方式。91吃瓜网的使命是让每一位吃瓜群众都能在第一时间获取最值得关注的瓜料资讯，不错过任何精彩瓜料动态。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed mt-3">
                  我们坚持客观、中立的报道原则，为读者呈现事实真相，同时也为读者提供表达观点和参与讨论的平台。
                </p>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: 'ri-article-line', num: '10万+', label: '瓜料资讯' },
              { icon: 'ri-user-smile-line', num: '500万+', label: '吃瓜群众' },
              { icon: 'ri-fire-line', num: '100万+', label: '日均浏览量' },
              { icon: 'ri-thumb-up-line', num: '99.7%', label: '用户好评率' },
            ].map((stat, i) => (
              <div key={i} className="bg-background-50 border border-background-200/70 rounded-xl p-5 text-center">
                <span className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center mx-auto mb-2">
                  <i className={`${stat.icon} text-lg text-primary-500`}></i>
                </span>
                <div className="text-2xl font-heading font-bold text-foreground-900">{stat.num}</div>
                <div className="text-xs text-foreground-600 mt-1">{stat.label}</div>
              </div>
            ))}
          </section>

          {/* Reputation */}
          <section className="mt-12">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 text-center mb-8">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  name: '吃瓜达人小王',
                  role: '资深吃瓜群众',
                  text: '自从发现91吃瓜网，每天第一件事就是打开看看有没有新瓜。信息更新很快，而且内容质量很高，不是那种乱编的假瓜。强烈推荐！',
                  rating: 5,
                },
                {
                  name: '娱乐圈观察者',
                  role: '自媒体博主',
                  text: '作为内容创作者，91吃瓜网是我获取选题灵感的重要来源。分类清晰，搜索方便，热点追踪也很及时，是我每天必刷的平台。',
                  rating: 5,
                },
                {
                  name: '热点追踪者小张',
                  role: '普通用户',
                  text: '界面简洁舒服，没有乱七八糟的广告。吃瓜体验很好，还能看到其他吃瓜群众的评论，感觉找到了组织。',
                  rating: 4,
                },
              ].map((review, i) => (
                <div key={i} className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <span key={j} className="w-4 h-4 flex items-center justify-center">
                        <i className={`text-sm ${j < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">{review.text}</p>
                  <div className="flex items-center gap-3 pt-3 border-t border-background-200/50">
                    <span className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-xs font-bold text-primary-500">
                      {review.name.charAt(0)}
                    </span>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{review.name}</div>
                      <div className="text-xs text-foreground-500">{review.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Section */}
          <section className="mt-12 mb-12">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 text-center mb-8">联系我们</h2>
            <div className="max-w-2xl mx-auto">
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
                <p className="text-sm text-foreground-600 text-center mb-6">
                  如果您有任何问题、建议或商务合作意向，欢迎通过以下方式联系我们
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  {[
                    { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@91chigua.com' },
                    { icon: 'ri-wechat-line', label: '微信公众号', value: '91吃瓜网' },
                    { icon: 'ri-time-line', label: '工作时间', value: '周一至周日 9:00-21:00' },
                  ].map((item, i) => (
                    <div key={i} className="text-center p-3 rounded-lg bg-background-100">
                      <span className="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center mx-auto mb-2">
                        <i className={`${item.icon} text-primary-500`}></i>
                      </span>
                      <div className="text-xs text-foreground-500 mb-1">{item.label}</div>
                      <div className="text-sm font-medium text-foreground-800">{item.value}</div>
                    </div>
                  ))}
                </div>

                <form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                  data-readdy-form
                  id="contact-form"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-xs font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-xs font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 transition-colors resize-none"
                      placeholder="请输入您的留言内容（不超过500字）"
                    />
                    <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
                  </div>

                  <div className="honeypot-field">
                    <label htmlFor="phone_alt">Phone</label>
                    <input
                      type="text"
                      id="phone_alt"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    发送留言
                  </button>

                  {formStatus === 'success' && (
                    <div className="p-3 bg-accent-50 border border-accent-200/70 rounded-lg text-sm text-accent-700 text-center">
                      感谢您的留言，我们会尽快回复！
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="p-3 bg-primary-50 border border-primary-200/70 rounded-lg text-sm text-primary-700 text-center">
                      {formError || '提交失败，请稍后重试'}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}